/**
 * Marquee Plugin
 */

export default (() => {
  // Helper functions
  function byClass(selClass, parent = document) {
    return parent.getElementsByClassName(selClass)
  }

  function query(selector, parent = document) {
    return parent.querySelector(selector)
  }

  function queryAll(selector, parent = document) {
    return parent.querySelectorAll(selector)
  }

  function forEachHTML(els, fn, scope) {
    for (let i = 0, numEls = els.length; i < numEls; i++) {
      fn.call(scope, els[i], i)
    }
  }

  function addClass(el, ...classes) {
    if (el.length === undefined) {
      addClassEl(el, ...classes)
    } else {
      forEachHTML(el, (currEl) => {
        addClassEl(currEl, ...classes)
      })
    }

    function addClassEl(elem, ...remClass) {
      remClass.forEach((singleClass) => {
        elem.classList.add(singleClass)
      })
    }
  }

  function addEvent(el, ev, fn, opts) {
    el.addEventListener(ev, fn, opts)
  }

  function removeEvent(el, ev, fn, opts) {
    el.removeEventListener(ev, fn, opts)
  }

  function getAttr(el, attr) {
    return el.getAttribute(attr)
  }

  function setAttr(el, attr, val) {
    el.setAttribute(attr, val)
  }

  function remAttr(el, attr) {
    el.removeAttribute(attr)
  }

  // Default options
  const defOpts = {
    css3easing: 'linear',
    delayBeforeStart: 1000,
    direction: 'left',
    duplicated: false,
    duration: 5000,
    gap: 20,
    pauseOnHover: false,
    recalcResize: false,
    speed: 0,
    startVisible: false,
  }

  let instances = 0

  /**
   * Marquee class
   */
  class Marquee {
    constructor(el, opts) {
      if (typeof el === 'undefined') throw new Error('el cannot be undefined')

      if (typeof el === 'string')
        throw new Error('el cannot be just a selector')

      if (el === null) throw new Error('el cannot be null')

      opts = {
        ...defOpts,
        ...opts,
      }

      this.el = el
      this._loopCount = 3

      // Check for data attributes
      for (const option in defOpts) {
        let currData = getAttr(el, `data-${option}`)

        if (currData !== null && currData !== '') {
          if (currData === 'true' || currData === 'false')
            currData = currData === 'true'

          opts[option] = currData
        }
      }

      // Calculate duration from speed if provided
      if (opts.speed)
        opts.duration = (parseInt(el.clientWidth) / opts.speed) * 1000

      opts.gap = opts.duplicated ? parseInt(opts.gap) : 0

      // Wrap content
      el.innerHTML = `<div class="js-marquee">${el.innerHTML}</div>`

      const marq = byClass('js-marquee', el)[0]
      marq.style.marginRight = `${opts.gap}px`
      marq.style.willChange = 'transform'
      marq.style.float = 'left'

      if (opts.duplicated) el.appendChild(marq.cloneNode(true))

      el.innerHTML = `<div style="width:100000px" class="js-marquee-wrapper">${el.innerHTML}</div>`

      const marqWrap = byClass('js-marquee-wrapper', el)[0]
      const vertical = opts.direction === 'up' || opts.direction === 'down'

      this._marqWrap = marqWrap
      this._vertical = vertical
      this._duration = opts.duration
      this._opts = opts

      this._calcSizes()

      const animationName = `marqueeAnimation-${Math.floor(Math.random() * 10000000)}`
      const animStr = this._animationStr(
        animationName,
        opts.duration / 1000,
        opts.delayBeforeStart / 1000,
        'infinite'
      )

      this._animName = animationName
      this._animStr = animStr

      // Position wrapper based on options
      if (opts.duplicated) {
        if (vertical) {
          if (opts.startVisible)
            this._marqWrap.style.transform = 'translateY(0px)'
          else
            this._marqWrap.style.transform = `translateY(${opts.direction === 'up' ? this._contHeight : -1 * (this._elHeight * 2 - opts.gap)}px)`
        } else {
          if (opts.startVisible)
            this._marqWrap.style.transform = 'translateX(0px)'
          else
            this._marqWrap.style.transform = `translateX(${opts.direction === 'left' ? this._contWidth : -1 * (this._elWidth * 2 - opts.gap)}px)`
        }

        if (!opts.startVisible) this._loopCount = 1
      } else if (opts.startVisible) {
        this._loopCount = 2
      } else {
        if (vertical) this._repositionVert()
        else this._repositionHor()
      }

      // Event listeners
      addEvent(this.el, 'pause', this.pause.bind(this))
      addEvent(this.el, 'resume', this.resume.bind(this))

      if (opts.pauseOnHover) {
        addEvent(this.el, 'mouseover', this.pause.bind(this))
        addEvent(this.el, 'mouseout', this.resume.bind(this))
      }

      this._animEnd = () => {
        this._animate(vertical)
        this.el.dispatchEvent(new CustomEvent('finished'))
      }

      this._instance = instances
      instances++

      this._animate(vertical)

      if (opts.recalcResize)
        addEvent(window, 'resize', this._recalcResize.bind(this))
    }

    _animationStr(name = '', duration = 0, delay = 0, loops = '') {
      return `${name} ${duration}s ${delay}s ${loops} ${this._opts.css3easing}`
    }

    _animate(vertical = false) {
      const opts = this._opts

      if (opts.duplicated) {
        if (this._loopCount === 1) {
          let duration = opts.duration

          if (vertical)
            duration =
              opts.direction === 'up'
                ? duration + this._contHeight / (this._elHeight / duration)
                : duration * 2
          else
            duration =
              opts.direction === 'left'
                ? duration + this._contWidth / (this._elWidth / duration)
                : duration * 2

          this._animStr = this._animationStr(
            this._animName,
            duration / 1000,
            opts.delayBeforeStart / 1000
          )
        } else if (this._loopCount === 2) {
          this._animName = `${this._animName}0`
          this._animStr = this._animationStr(
            this._animName,
            opts.duration / 1000,
            0,
            'infinite'
          )
        }

        this._loopCount++
      }

      let animationCss = ''

      if (vertical) {
        if (opts.duplicated) {
          if (this._loopCount > 2)
            this._marqWrap.style.transform = `translateY(${opts.direction === 'up' ? 0 : -1 * this._elHeight}px)`

          animationCss = `translateY(${opts.direction === 'up' ? -1 * this._elHeight : 0}px)`
        } else if (opts.startVisible) {
          if (this._loopCount === 2) {
            this._animStr = this._animationStr(
              this._animName,
              opts.duration / 1000,
              opts.delayBeforeStart / 1000
            )
            animationCss = `translateY(${opts.direction === 'up' ? -1 * this._elHeight : this._contHeight}px)`
            this._loopCount++
          } else if (this._loopCount === 3) {
            this._animName = `${this._animName}0`
            this._animStr = this._animationStr(
              this._animName,
              this._completeDuration / 1000,
              0,
              'infinite'
            )
            this._repositionVert()
          }
        } else {
          this._repositionVert()
          animationCss = `translateY(${opts.direction === 'up' ? -1 * this._marqWrap.clientHeight : this._contHeight}px)`
        }
      } else {
        if (opts.duplicated) {
          if (this._loopCount > 2)
            this._marqWrap.style.transform = `translateX(${opts.direction === 'left' ? 0 : -1 * this._elWidth}px)`

          animationCss = `translateX(${opts.direction === 'left' ? -1 * this._elWidth : 0}px)`
        } else if (opts.startVisible) {
          if (this._loopCount === 2) {
            this._animStr = this._animationStr(
              this._animName,
              opts.duration / 1000,
              opts.delayBeforeStart / 1000
            )
            animationCss = `translateX(${opts.direction === 'left' ? -1 * this._elWidth : this._contWidth}px)`
            this._loopCount++
          } else if (this._loopCount === 3) {
            this._animName = `${this._animName}0`
            this._animStr = this._animationStr(
              this._animName,
              opts.duration / 1000,
              0,
              'infinite'
            )
            this._repositionHor()
          }
        } else {
          this._repositionHor()
          animationCss = `translateX(${opts.direction === 'left' ? -1 * this._elWidth : this._contWidth}px)`
        }
      }

      this.el.dispatchEvent(new CustomEvent('beforeStarting'))

      this._marqWrap.style.animation = this._animStr

      const keyFrameCss = `@keyframes ${this._animName} {
        100% {
          transform: ${animationCss};
        }
      }`

      const styles = queryAll('style', this._marqWrap)

      if (styles.length) styles[styles.length - 1].innerHTML = keyFrameCss
      else if (byClass(`marq-wrap-style-${this._instance}`).length)
        byClass(`marq-wrap-style-${this._instance}`)[0].innerHTML = keyFrameCss
      else {
        const styleEl = document.createElement('style')
        addClass(styleEl, `marq-wrap-style-${this._instance}`)
        styleEl.innerHTML = keyFrameCss
        query('head').appendChild(styleEl)
      }

      addEvent(
        this._marqWrap,
        'animationiteration',
        this._animIter.bind(this),
        { once: true }
      )
      addEvent(this._marqWrap, 'animationend', this._animEnd.bind(this), {
        once: true,
      })

      this._status = 'running'
      setAttr(this.el, 'data-runningStatus', 'resumed')
    }

    _animIter() {
      this.el.dispatchEvent(new CustomEvent('finished'))
    }

    _repositionVert() {
      this._marqWrap.style.transform = `translateY(${this._opts.direction === 'up' ? this._contHeight : this._elHeight * -1}px)`
    }

    _repositionHor() {
      this._marqWrap.style.transform = `translateX(${this._opts.direction === 'left' ? this._contWidth : this._elWidth * -1}px)`
    }

    _calcSizes() {
      const el = this.el
      const opts = this._opts

      if (this._vertical) {
        const contHeight = el.clientHeight
        this._contHeight = contHeight

        remAttr(this._marqWrap, 'style')
        el.style.clientHeight = `${contHeight}px`

        const marqs = byClass('js-marquee', el)
        const marqNums = marqs.length - 1

        forEachHTML(marqs, (currEl, ind) => {
          currEl.style.float = 'none'
          currEl.style.marginRight = '0px'

          if (opts.duplicated && ind === marqNums)
            currEl.style.marginBottom = '0px'
          else currEl.style.marginBottom = `${opts.gap}px`
        })

        const elHeight = parseInt(marqs[0].clientHeight + opts.gap)
        this._elHeight = elHeight

        if (opts.startVisible && !opts.duplicated) {
          this._completeDuration =
            ((elHeight + contHeight) / parseInt(contHeight)) * this._duration
          opts.duration = (elHeight / parseInt(contHeight)) * this._duration
        } else {
          opts.duration =
            (elHeight / parseInt(contHeight) / parseInt(contHeight)) *
            this._duration
        }
      } else {
        const elWidth = parseInt(
          byClass('js-marquee', el)[0].clientWidth + opts.gap
        )
        const contWidth = el.clientWidth

        this._contWidth = contWidth
        this._elWidth = elWidth

        if (opts.startVisible && !opts.duplicated) {
          this._completeDuration =
            ((elWidth + contWidth) / parseInt(contWidth)) * this._duration
          opts.duration = (elWidth / parseInt(contWidth)) * this._duration
        } else {
          opts.duration =
            ((elWidth + parseInt(contWidth)) / parseInt(contWidth)) *
            this._duration
        }
      }

      if (opts.duplicated) opts.duration = opts.duration / 2
    }

    _recalcResize() {
      this._calcSizes()
      this._loopCount = 2
      this._animEnd()
    }

    pause() {
      this._marqWrap.style.animationPlayState = 'paused'
      this._status = 'paused'
      setAttr(this.el, 'data-runningStatus', 'paused')
      this.el.dispatchEvent(new CustomEvent('paused'))
    }

    resume() {
      this._marqWrap.style.animationPlayState = 'running'
      this._status = 'running'
      setAttr(this.el, 'data-runningStatus', 'resumed')
      this.el.dispatchEvent(new CustomEvent('resumed'))
    }

    toggle() {
      if (this._status === 'paused') this.resume()
      else if (this._status === 'running') this.pause()
    }

    destroy() {
      removeEvent(this.el, 'pause', this.pause.bind(this))
      removeEvent(this.el, 'resume', this.resume.bind(this))

      if (this._opts.pauseOnHover) {
        removeEvent(this.el, 'mouseover', this.pause.bind(this))
        removeEvent(this.el, 'mouseout', this.resume.bind(this))
      }

      removeEvent(
        this._marqWrap,
        'animationiteration',
        this._animIter.bind(this),
        { once: true }
      )
      removeEvent(this._marqWrap, 'animationend', this._animEnd.bind(this), {
        once: true,
      })

      if (this._opts.recalcResize)
        removeEvent(window, 'resize', this._recalcResize.bind(this))
    }

    refresh() {
      this._recalcResize()
    }
  }

  // Expose to global scope
  window.Marquee = Marquee
})()
